import { printVisibility } from '@/features/styles/lib/printStyles';
import { Box, Portal } from '@/ui';
import type { AnchorId } from './steps';
import { useAnchorRect } from './useAnchorRect';

type Props = {
  anchorId: AnchorId;
  /** Lift over the Share modal, whose overlay would otherwise bury the ring. */
  isAboveModal?: boolean;
  /** Dim everything outside the ring. Off for cards that dim by region instead. */
  hasScrim?: boolean;
};

const GLOW = '0 0 0 4px rgba(93, 63, 246, 0.18)';

// One spread large enough to reach every corner of any viewport dims the whole
// page, and the element's own box is the hole left undimmed. Cheaper than four
// rects around the target, and it follows the same rounded corners and the same
// transition, so the lit area glides with the ring rather than being redrawn.
const SCRIM = '0 0 0 100vmax rgba(17, 12, 40, 0.38)';

/**
 * Traces the control the current step is talking about. Drawn as a fixed overlay
 * rather than a border on the control itself, so it needs no cooperation from
 * the target and cannot shift the bar's layout.
 */
export const SpotlightRing = ({
  anchorId,
  hasScrim = true,
  isAboveModal = false,
}: Props) => {
  const rect = useAnchorRect(anchorId);

  if (!rect) return null;

  return (
    <Portal>
      <Box
        border="2px solid"
        borderColor="magicPurple.500"
        borderRadius="ah-lg"
        boxShadow={hasScrim ? `${GLOW}, ${SCRIM}` : GLOW}
        data-onboarding-ring=""
        height={`${rect.height + 8}px`}
        left={`${rect.left - 4}px`}
        pointerEvents="none"
        position="fixed"
        sx={printVisibility.hide()}
        top={`${rect.top - 4}px`}
        transition="top 0.15s ease, left 0.15s ease"
        width={`${rect.width + 8}px`}
        zIndex={isAboveModal ? 'calc(var(--chakra-zIndices-modal) + 1)' : 24}
      />
    </Portal>
  );
};
